// The one dialog that asks "should I kill this browser".
//
// Three call sites used to disagree about this: the Fleet page showed a bundle
// path, the live sessions list and the session viewer killed on the first
// click with no question at all. They now share this, so the answer to "which
// browser am I about to lose" is the same wherever it is asked.
//
// Two things it insists on. It names the session by what the browser presents
// as (`Android 13 · Mali-G57 · M2103K19C`) rather than by a path or a uuid,
// and it asks the worker on open whether the session is still there — a
// confirmation that promises to destroy automation state has no business
// saying so about a browser that exited an hour ago.

import { useQuery } from "@tanstack/react-query";
import { Badge } from "@veil/ui/components/badge";
import {
	ResponsiveDialog,
	ResponsiveDialogAction,
	ResponsiveDialogCancel,
	ResponsiveDialogContent,
	ResponsiveDialogDescription,
	ResponsiveDialogFooter,
	ResponsiveDialogHeader,
	ResponsiveDialogTitle,
} from "@veil/ui/components/responsive-dialog";
import { Skeleton } from "@veil/ui/components/skeleton";
import { useState } from "react";
import { api, unwrap } from "@/lib/api";
import {
	describeSession,
	fingerprintFromBundlePath,
	formatUptime,
	isSessionGone,
	type SessionFingerprint,
	shortId,
} from "@/lib/session-identity";

/**
 * Which of the worker's two id spaces the caller holds.
 *
 * They do not overlap: `launch` is the launch broker's `clusterRegistry`, which
 * is what the live sessions list and the viewer carry, while `session` is the
 * REST session store behind the Fleet page's recent-launches table. Naming the
 * space here is what lets one dialog serve both.
 */
export type StopSessionTarget =
	| {
			kind: "launch";
			hostname: string;
			/** clusterRegistry launch id. */
			id: string;
			/**
			 * The same browser's REST session id, when the caller knows it.
			 *
			 * The registry forgets a browser the instant it exits, so a launch id
			 * alone can only ever answer "gone". This is the join into the store
			 * that kept the row — and the fingerprint on it.
			 */
			sessionId?: string | null;
			referenceId?: string | null;
			/** Known from the list, saves a round trip before the fetch lands. */
			uptime?: number | null;
			connectionCount?: number | null;
	  }
	| {
			kind: "session";
			hostname: string;
			/** REST session id. */
			id: string;
			/** Last-known bundle path, parsed only if the worker cannot answer. */
			profilePath?: string | null;
			startedAt?: string | null;
	  };

interface ResolvedIdentity {
	fingerprint: SessionFingerprint | null;
	/** Null when the endpoint does not report it (the launch-id space). */
	status: string | null;
	uptime: number | null;
	connectionCount: number | null;
	referenceId: string | null;
	endedAt: string | null;
	/**
	 * Set only where the answer is known at the point it was fetched and is not
	 * recoverable from `status` afterwards: the registry 404'd for a launch id,
	 * and the identity below came from the session store instead. The store can
	 * still be a beat behind on the row's status, and the registry's answer is
	 * the authoritative one for a launch id.
	 */
	gone?: boolean;
}

/** Fields of the REST session store this dialog reads. */
interface RestSession {
	status: string | null;
	startedAt: string | null;
	endedAt: string | null;
	fingerprint: SessionFingerprint | null;
}

/**
 * Ask the worker who this is and whether it is still running.
 *
 * Only while the dialog is open, and never on a timer: this exists to describe
 * a decision the operator is making right now, not to keep a background poll
 * alive on every worker holding a browser.
 */
function useSessionIdentity(target: StopSessionTarget | null, attempt: number) {
	return useQuery<ResolvedIdentity>({
		queryKey: [
			"session-identity",
			target?.hostname,
			target?.kind,
			target?.id,
			// Bumped on every open, so reopening the same session asks the worker
			// again rather than reusing an answer from a minute ago. Liveness is
			// the whole point; a cached "still running" would defeat it.
			attempt,
		],
		enabled: !!target,
		retry: false,
		staleTime: 0,
		queryFn: async () => {
			if (!target) throw new Error("No session selected");
			const { hostname, id } = target;

			if (target.kind === "launch") {
				try {
					const res = (await unwrap(
						api.api.workers({ hostname }).browsers({ launchId: id }).info.get(),
					)) as unknown as {
						data: Omit<ResolvedIdentity, "status" | "endedAt">;
					};
					return {
						fingerprint: res.data.fingerprint ?? null,
						// The registry only holds browsers that are up, so a 200
						// here is itself the liveness answer.
						status: "running",
						uptime: res.data.uptime ?? null,
						connectionCount: res.data.connectionCount ?? null,
						referenceId: res.data.referenceId ?? null,
						endedAt: null,
					};
				} catch (err) {
					// A 404 means the browser exited, which is precisely when
					// naming it matters and precisely when the registry can no
					// longer do it. The session store kept the row, so ask that
					// instead — but only for a 404: a worker that could not be
					// reached has told us nothing, and a second request to the
					// same unreachable worker would only tell us nothing twice.
					const restId = target.sessionId;
					const message = err instanceof Error ? err.message : String(err);
					if (!restId || !isSessionGone({ errorMessage: message })) throw err;
					const stored = await readRestSession(hostname, restId).catch(
						() => null,
					);
					// Rethrowing keeps the outcome identical to before this
					// fallback existed: the dialog reads "gone" off the 404.
					if (!stored) throw err;
					return {
						fingerprint: stored.fingerprint,
						status: stored.status,
						uptime: null,
						connectionCount: null,
						referenceId: null,
						endedAt: stored.endedAt,
						gone: true,
					};
				}
			}

			const stored = await readRestSession(hostname, id);
			return {
				fingerprint: stored.fingerprint,
				status: stored.status,
				uptime:
					stored.status === "running" && stored.startedAt
						? Math.max(
								0,
								Math.round(
									(Date.now() - new Date(stored.startedAt).getTime()) / 1000,
								),
							)
						: null,
				connectionCount: null,
				referenceId: null,
				endedAt: stored.endedAt,
			};
		},
	});
}

async function readRestSession(
	hostname: string,
	sessionId: string,
): Promise<RestSession> {
	const res = (await unwrap(
		api.api.workers({ hostname }).sessions({ sessionId }).get(),
	)) as unknown as { data: RestSession };
	return {
		status: res.data.status ?? null,
		startedAt: res.data.startedAt ?? null,
		endedAt: res.data.endedAt ?? null,
		fingerprint: res.data.fingerprint ?? null,
	};
}

/** Worker, how long it has run, who is attached, and enough id to cross-check. */
function ProvenanceRow({
	hostname,
	uptime,
	connectionCount,
	referenceId,
	id,
}: {
	hostname: string;
	uptime: number | null;
	connectionCount: number | null;
	referenceId: string | null;
	id: string;
}) {
	const bits: string[] = [hostname];
	if (uptime != null) bits.push(`up ${formatUptime(uptime)}`);
	if (referenceId) bits.push(referenceId);
	else bits.push(shortId(id));

	return (
		<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
			<span className="font-mono text-xs text-muted-foreground">
				{bits.join(" · ")}
			</span>
			{connectionCount != null ? (
				// Variant plus text, never colour alone.
				<Badge variant={connectionCount > 0 ? "success" : "outline"}>
					{connectionCount > 0
						? `${connectionCount} client${connectionCount === 1 ? "" : "s"} attached`
						: "no client attached"}
				</Badge>
			) : null}
		</div>
	);
}

export function StopSessionDialog({
	target,
	onOpenChange,
	onConfirm,
	pending,
}: {
	target: StopSessionTarget | null;
	onOpenChange: (open: boolean) => void;
	onConfirm: (target: StopSessionTarget) => void;
	pending: boolean;
}) {
	// The dialog animates out after `target` clears, and rendering the empty
	// state through that animation makes it flash "Unidentified browser" on the
	// way down. Hold the last subject so the closing frame still shows what was
	// being asked about. `attempt` rises with each open and keys the liveness
	// query, so holding the subject does not also hold a stale answer.
	const [shown, setShown] = useState<StopSessionTarget | null>(target);
	const [attempt, setAttempt] = useState(0);
	if (target && target !== shown) {
		setShown(target);
		setAttempt((n) => n + 1);
	}

	const identity = useSessionIdentity(target, attempt);

	// Derived during render: mirroring this into state would let the label drift
	// from the query that produced it.
	const fallback =
		shown?.kind === "session"
			? fingerprintFromBundlePath(shown.profilePath)
			: null;
	const described = describeSession(identity.data?.fingerprint ?? fallback);

	const gone =
		identity.data?.gone === true ||
		isSessionGone({
			status: identity.data?.status,
			errorMessage: identity.isError ? identity.error.message : null,
		});

	// The worker answered nothing at all (unreachable, timed out). Distinct from
	// "gone": we must not claim the session ended when we simply could not ask.
	const unverified = identity.isError && !gone;

	// What the caller already knew. The fetch is still worth making — it is the
	// only source of the fingerprint, and the only liveness check — but the
	// provenance line does not have to sit behind a skeleton waiting for it.
	// Uptime is dropped once the session is known to be over: the last figure
	// the list saw would read as if the browser were still counting.
	const known = shown?.kind === "launch" ? shown : null;
	const uptime =
		identity.data?.uptime ?? (gone ? null : (known?.uptime ?? null));
	const connectionCount =
		identity.data?.connectionCount ??
		(gone ? null : (known?.connectionCount ?? null));
	const referenceId = identity.data?.referenceId ?? known?.referenceId ?? null;

	const title = gone ? "This session has already ended" : "Stop this session?";

	return (
		<ResponsiveDialog
			variant="alert"
			open={!!target}
			onOpenChange={(open) => {
				if (!open) onOpenChange(false);
			}}
		>
			<ResponsiveDialogContent>
				<ResponsiveDialogHeader>
					<ResponsiveDialogTitle>{title}</ResponsiveDialogTitle>

					{/* The identity is the subject of the question, so it leads and it
					    is set in the reading face. Ids stay monospace below it. */}
					<div className="mt-1 space-y-1.5">
						{identity.isLoading ? (
							<Skeleton className="h-5 w-56" />
						) : /* "Unidentified browser" is an admission, and it is only
						      worth making while we are still asking the operator to
						      approve something. Once the answer is "it already
						      ended" there is nothing to identify and the line is
						      just noise above a message that already says so. */
						described.label ? (
							<p className="text-sm font-medium text-foreground">
								{described.label}
							</p>
						) : gone ? null : (
							<p className="text-sm font-medium text-muted-foreground">
								Unidentified browser
							</p>
						)}
						{shown ? (
							<ProvenanceRow
								hostname={shown.hostname}
								uptime={uptime}
								connectionCount={connectionCount}
								referenceId={referenceId}
								id={shown.id}
							/>
						) : null}
					</div>

					<ResponsiveDialogDescription className="mt-2">
						{gone ? (
							"The worker no longer has this browser, so there is nothing to stop. The list will catch up on its next poll."
						) : unverified ? (
							<>
								{shown?.hostname} did not answer, so these details are the last
								ones recorded and may be stale. Stopping is still attempted, and
								does nothing if the browser has already exited.
							</>
						) : (
							<>
								The browser is terminated immediately. Unsaved automation state
								is lost, and a one-time profile is deleted with it.
							</>
						)}
					</ResponsiveDialogDescription>
				</ResponsiveDialogHeader>

				<ResponsiveDialogFooter>
					{gone ? (
						<ResponsiveDialogCancel onClick={() => onOpenChange(false)}>
							Close
						</ResponsiveDialogCancel>
					) : (
						<>
							<ResponsiveDialogCancel disabled={pending}>
								Keep running
							</ResponsiveDialogCancel>
							<ResponsiveDialogAction
								variant="destructive"
								disabled={pending}
								onClick={(e) => {
									e.preventDefault();
									if (shown) onConfirm(shown);
								}}
							>
								{pending ? "Stopping…" : "Stop session"}
							</ResponsiveDialogAction>
						</>
					)}
				</ResponsiveDialogFooter>
			</ResponsiveDialogContent>
		</ResponsiveDialog>
	);
}
